/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
import { Command } from '../../../model/Command';
import { Filter } from '../../../model/Filter';
import { ListController } from '../../ListController';

class PreventiveSheetListController extends ListController {
    /** @ngInject */
    constructor(preventiveSheetService, waitSpinnerService, $q, $state, $stateParams, $log, notificationService,
        dateTimeService, configuration, domainService, siteService, equipmentService, equipmentTemplateService,
        filterService, counterService, $scope) {
        super(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log);
        this.preventiveSheetService = preventiveSheetService;
        this.domainService = domainService;
        this.siteService = siteService;
        this.equipmentService = equipmentService;
        this.equipmentTemplateService = equipmentTemplateService;
        this.filterService = filterService;
        this.dateTimeService = dateTimeService;
        this.counterService = counterService;
        this.$scope = $scope;
        // mdDataTable doesn't support ES6 syntax with $ctrl variable in templates. That's why we must attach the reload
        // callback to the scope with an arrow function so as it can be called by this component with the correct 'this'
        // binding.
        this.$scope.reload = () => this.reload();
        this.$log.debug('Controller \'PreventiveSheetList\' loaded');
    }

    $onInit() {
        super.$onInit();

        this.waitSpinnerService.visible = true;
        this.paging.sort = 'maxActionDate';
        this.commands = [];
        this.initFilters();

        this.handleRestServiceCall(this.loadFilters(), () => {
            this.initCommands();
            this.reload();
        }).finally(() => {
            this.waitSpinnerService.visible = false;
        });
    }

    /**
     * Initializes the filters available at the top of the view.
     */
    initFilters() {
        this.equipmentFilter = Filter.equalFilterWithValue('equipment.id', this.$stateParams.filter.equipmentId,
            'model.equipment.equipment', 'SELECT', []);

        this.siteFilter = Filter.equalFilterWithValue('equipment.site.id', this.$stateParams.filter.siteId,
            'model.site.site', 'SELECT_AUTOCOMPLETE', [], siteId => this.getFilterEquipments(siteId));

        this.domainFilter = Filter.equalFilterWithValue('equipment.site.domain.id', this.$stateParams.filter.domainId,
            'model.domain.domain', 'SELECT', [], domainId => this.getFilterSites(domainId)
                .then(() => this.getFilterEquipmentTemplates(domainId)));

        this.equipmentTemplateFilter = Filter.equalFilterEmpty('equipment.equipmentTemplate.id',
            'model.equipmenttemplate.equipmenttemplate', 'SELECT', []);

        let preventiveSheetStatuses;
        if (this.$stateParams.filter.equipmentId === undefined) {
            // all preventive sheets
            preventiveSheetStatuses = ['OPENED'];
        } else {
            // preventive sheets for 1 equipment
            preventiveSheetStatuses = [];
        }

        this.filters.push(
            Filter.equalFilterEmpty('id', 'ui.id', 'NUMBER'),
            Filter.preventiveSheetStatusFilter(preventiveSheetStatuses),
            Filter.emptyLikeFilter('equipment.code', 'model.equipment.code'),
            this.domainFilter,
            this.equipmentTemplateFilter,
            this.siteFilter,
            this.equipmentFilter,
            Filter.greaterOrEqualDateFilter('maxActionDate', undefined, 'ui.preventivesheet.plannedfrom',
                filter => this.toLocalDate(filter)),
            Filter.lowerOrEqualDateFilter('maxActionDate', undefined, 'ui.preventivesheet.plannedto',
                filter => this.toLocalDate(filter))
        );
    }

    /**
     * Initializes the commands available in the view header.
     */
    initCommands() {
        this.commands.push(
            Command.stateCommand('ui.preventivesheet.create', 'images/svg/add.svg',
                'gmao.preventivesheet.create', ['ADMIN', 'PREVENTIVE_SHEET_CREATE']),
            Command.handlerCommand(undefined, 'images/svg/reset.svg', () => this.reload())
        );
    }

    /**
     * Loads data in filters.
     */
    loadFilters() {
        return this.getFilterDomains()
            .then(() => this.getFilterSites(this.domainFilter.value))
            .then(() => this.getFilterEquipments(this.siteFilter.value))
            .then(() => this.getFilterEquipmentTemplates(this.domainFilter.value));
    }

    /**
     * Gets the list of domains.
     *
     * @return {Object[]} Promise providing an array of domains.
     */
    getFilterDomains() {
        return this.filterService.getFilterOptions(this.domainService, this.domainFilter, 'name');
    }

    /**
     * Gets the list of sites for a given domain.
     *
     * @param {Number} [domainId] Domain ID, may be undefined if no domain is selected.
     * @return {Object[]} Promise providing an array of sites.
     */
    getFilterSites(domainId) {
        return this.filterService.getOrClearFilterOptions(this.siteService, this.siteFilter, 'name',
            [Filter.equalFilter('domain.id', domainId)], domainId)
            .then(() => this.getFilterEquipments(this.siteFilter.value));
    }

    /**
     * Gets the list of equipments for a given site.
     *
     * @param {Number} [siteId] Site ID, may be undefined if no site is selected.
     * @return {Object[]} Promise providing an array of equipments.
     */
    getFilterEquipments(siteId) {
        return this.filterService.getOrClearFilterOptions(this.equipmentService, this.equipmentFilter, 'name',
            [Filter.equalFilter('site.id', siteId)], siteId);
    }

    /**
     * Gets the list of equipment templates for a given domain.
     *
     * @param {Number} [domainId] Domain ID, may be undefined if no domain is selected.
     * @return {Object[]} Promise providing an array of equipment templates.
     */
    getFilterEquipmentTemplates(domainId) {
        return this.filterService.getOrClearFilterOptions(this.equipmentTemplateService, this.equipmentTemplateFilter,
            'name', [Filter.equalFilter('domain.id', domainId)], domainId);
    }

    /**
     * Transforms a date filter to a local date value, before serialization in an HTTP request.
     *
     * @param {Filter} filter Filter.
     */
    toLocalDate(filter) {
        filter.value = this.dateTimeService.formatLocalDate(filter.value);
    }

    /**
     * Check if the preventive sheet is edited
     *
     * @param creation date string
     * @param update date string
     * @return {boolean}
     */
    isEdited(creation, update) {
        let creationDate = new Date(creation);
        let updateDate = new Date(update);
        updateDate.setHours(updateDate.getHours() -1);
        return updateDate > creationDate;
    }

    /**
     * Reloads the list of entities.
     *
     * @return {Promise} A promise providing the list of entities.
     */
    reload() {
        this.counterService.reloadCount();
        return this.handleRestServiceCall(
            this.preventiveSheetService.searchEntities({
                params: this.filterService.buildPagingParams(this.paging),
                data: this.filterService.buildFilters(this.filters)
            }),
            data => {
                this.model.data = data.content;
                this.model.data.forEach(preventiveSheet => {
                    preventiveSheet.plannedWeek = this.dateTimeService.toPreventiveSheetPlannedWeek(
                        preventiveSheet.maxActionDate);
                });
                this.model.totalElements = data.totalElements;
            }
        );
    }
}

export const preventiveSheetListComponent = {
    template: require('./preventivesheet-list.html'),
    controller: PreventiveSheetListController
};



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/prevention/component/PreventiveSheetList.js